import React, { useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import { IconHeart, IconUser, IconUsers, IconAdaptive } from '@edulastic/icons'
import { accessibilityColors, cardTitleColor } from '@edulastic/colors'
import { LikeIconStyled } from '@edulastic/common'
import { PEAR_ASSESSMENT_CERTIFIED_NAME } from '@edulastic/constants/const/common'
import { testCategoryTypes } from '@edulastic/constants/const/test'
import { sanitizeImgUrl } from '@edulastic/common/src/utils/html'
import {
  Container,
  Inner,
  Footer,
  Author,
  AuthorName,
  Header,
  StyledLink,
  TestInfo,
  ShareIcon,
  AuthorWrapper,
  IconText,
  TagsWrapper,
  PlaylistId,
  StatusRow,
  Qcount,
  MidRow,
  Collection,
  CollectionNameWrapper,
  TestTypeCol,
  RecommendationTypeBadge,
} from './styled'
import { getTestType } from './testType'
import Tags from '../../../src/components/common/Tags'
import { TestStatus } from '../ListItem/styled'
import { getAuthorCollectionMap } from '../../../dataUtils'
import TestStatusWrapper from '../TestStatusWrapper/testStatusWrapper'
import AddRemoveTest from './AddRemoveTestToFolder'
import { onEnterKeyPressHandler } from '../../../../common/utils/helpers'

const TestItemCard = ({
  thumbnail,
  openModal,
  status,
  testId,
  collections,
  // showPremiumTag,
  standardsIdentifiers,
  title,
  collectionName,
  isDocBased,
  summary,
  isDynamicTest,
  authorName,
  testItemId,
  usage,
  isTestLiked,
  handleLikeTest,
  likes,
  isTestRecommendation,
  videoUrl,
  testCategory,
  aiGenerated,
  aiDocExtracted,
  importData,
}) => {
  const [height, setHeight] = useState(0)
  const ref = useRef(null)
  const testType = getTestType({
    aiGenerated,
    aiDocExtracted,
    isDocBased,
    videoUrl,
    isDynamicTest,
    importData,
  })
  useEffect(() => {
    setHeight(ref.current.clientHeight)
  }, [ref.current])

  // Open the test from content areas only — not the footer — so Like/folder
  // controls do not compete with a parent card onClick (ASM-782 / Firefox).
  return (
    <Container
      isTestCard
      src={sanitizeImgUrl(thumbnail)}
      isTestRecommendation={isTestRecommendation}
    >
      <OnHoverContainer data-cy="test-hover-container" onClick={openModal}>
        <Header
          isTest
          src={sanitizeImgUrl(thumbnail)}
          isTestRecommendation={isTestRecommendation}
        >
          {testCategory === testCategoryTypes.ADAPTIVE_SECTION_TEST && (
            <div style={{ position: 'absolute', bottom: 0, left: 5 }}>
              <IconAdaptive />
            </div>
          )}
          {collections.find((o) => o.name === PEAR_ASSESSMENT_CERTIFIED_NAME) &&
            getAuthorCollectionMap(false, 30, 30).edulastic_certified.icon}
          {/* hiding premium tag temporarily as per CR */}
          {/* {showPremiumTag && <PremiumLabel> PREMIUM</PremiumLabel>} */}
          {/* MidRow (with the TEST TYPE column) is not rendered for recommendations */}
          {isTestRecommendation && testType && (
            <RecommendationTypeBadge
              data-cy="test-type"
              data-test-type={testType.key}
              title={testType.label}
            >
              {testType.icon}
            </RecommendationTypeBadge>
          )}
        </Header>
        <TestInfo>
          <StyledLink ref={ref} data-cy="test-title" title={title}>
            {title}
          </StyledLink>
          <TagsWrapper testNameHeight={height} data-cy="test-standards">
            <Tags
              show={2}
              tags={standardsIdentifiers}
              key="standards"
              isStandards
              margin="0px"
              isTestCard
              flexWrap="noWrap"
              testId={testId}
            />
          </TagsWrapper>
        </TestInfo>

        {!isTestRecommendation && (
          <MidRow isTest>
            <Collection hasTestType={!!testType}>
              <label>COLLECTIONS</label>
              <CollectionNameWrapper
                data-cy="test-collection"
                title={collectionName}
              >
                {collectionName}
              </CollectionNameWrapper>
            </Collection>
            <Qcount>
              <label>{isDocBased ? 'TOTAL QUESTIONS' : 'TOTAL ITEMS'}</label>
              {/**
               * For doc based wee need to consider
               *  total number questions and toal number of items
               *  */}
              <div data-cy="test-item-count">
                {isDocBased ? summary.totalQuestions : summary.totalItems}
              </div>
            </Qcount>
            {testType && (
              <TestTypeCol data-cy="test-type" data-test-type={testType.key}>
                <label>TEST TYPE</label>
                <div title={testType.label}>{testType.icon}</div>
              </TestTypeCol>
            )}
          </MidRow>
        )}
      </OnHoverContainer>
      {!isTestRecommendation && (
        <Inner isTest onClick={openModal}>
          {authorName && (
            <Author>
              <AuthorWrapper>
                {collections.find(
                  (o) => o.name === PEAR_ASSESSMENT_CERTIFIED_NAME
                ) ? (
                  getAuthorCollectionMap(true, 30, 30).edulastic_certified.icon
                ) : (
                  <IconUser color={cardTitleColor} />
                )}
                <AuthorName data-cy="test-author-name" title={authorName}>
                  {authorName}
                </AuthorName>
              </AuthorWrapper>
            </Author>
          )}
          <StatusRow>
            <TestStatusWrapper status={status} checkUser={false}>
              {({ children, ...rest }) => (
                <TestStatus data-cy="test-status" {...rest} view="tile">
                  {children}
                </TestStatus>
              )}
            </TestStatusWrapper>
          </StatusRow>
        </Inner>
      )}

      {!isTestRecommendation && (
        <Footer height="33px">
          {testItemId ? (
            <PlaylistId data-cy="test-id">
              <span>#</span>
              <span>{testItemId}</span>
            </PlaylistId>
          ) : null}
          {status !== 'draft' && (
            <>
              <ShareIcon aria-label={`Used ${usage} times.`}>
                <IconUsers width={14} height={14} aria-hidden /> &nbsp;
                <IconText aria-hidden>{usage}</IconText>
              </ShareIcon>
              <LikeIconStyled
                tabIndex={0}
                role="button"
                aria-pressed={isTestLiked}
                title={isTestLiked ? 'Unlike' : 'Like'}
                isLiked={isTestLiked}
                onClick={handleLikeTest}
                onKeyDown={(e) => onEnterKeyPressHandler(e, handleLikeTest)}
                aria-label={
                  isTestLiked
                    ? `Unlike this test. It has ${likes} likes.`
                    : `Like this test. It has ${likes} likes.`
                }
              >
                <IconHeart
                  color={
                    isTestLiked
                      ? '#ca481e'
                      : accessibilityColors.testLibrary.footerIconText
                  }
                  width={14}
                  height={14}
                  aria-hidden
                />
                <IconText aria-hidden>{likes}</IconText>
                {isTestLiked && (
                  <span style={{ position: 'absolute', left: '-9999px' }}>
                    Liked by me
                  </span>
                )}
              </LikeIconStyled>
            </>
          )}
          <AddRemoveTest testDetail={{ itemId: testId, name: title }} />
        </Footer>
      )}
    </Container>
  )
}

export default TestItemCard

const OnHoverContainer = styled.div`
  &:hover {
    .showHoverTestCard {
      display: flex;
      justify-content: space-around;
      align-items: center;
      flex-direction: column;
      padding: 30px 0px;
    }
  }
`
